import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  PerformanceGoalsUpdateMetricDocument,
  PerformanceGoalsUpdateMetricFragment,
  PerformanceGoalsUpdateMetricMutationVariables,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { MetricGroupSelectField } from 'modules/goals/components/MetricGroupSelectField'
import {
  validateDate,
  validateGoalExistence,
  validateTitle,
} from 'modules/goals/lib/keyResultsValidations'
import {
  NormalizedQualitativeMetric,
  NormalizedQuantitativeMetric,
} from 'modules/goals/lib/normalizeMetric'

export const UpdateMetricModal: React.FC<{
  metric:
    | NormalizedQualitativeMetric<PerformanceGoalsUpdateMetricFragment>
    | NormalizedQuantitativeMetric<PerformanceGoalsUpdateMetricFragment>
  refetchQueries?: DocumentNode[]
  onClose: () => void
}> = ({ metric, onClose, refetchQueries }) => {
  const [update] = useMutation(PerformanceGoalsUpdateMetricDocument, {
    extractorFn: (data) => data.goalsMutations.updateGoalsMetric,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  const form = useForm<PerformanceGoalsUpdateMetricMutationVariables>({
    defaultValues: {
      id: metric.id,
      startOn: metric.startOn,
      endOn: metric.endOn,
      title: metric.title,
      startValue: metric.type === 'quantitative' ? metric.quantitativeValue.startValue : undefined,
      targetValue:
        metric.type === 'quantitative' ? metric.quantitativeValue.targetValue : undefined,
      metricGroupId: metric.metricGroup?.id || null,
    },

    validate(values) {
      const errors: Partial<typeof values> = {}

      const titleError = validateTitle(values.title)
      if (titleError) {
        errors.title = titleError
      }

      const dateError = validateDate(values.startOn, values.endOn)
      if (dateError) {
        errors.endOn = dateError
      }

      const goalError = validateGoalExistence(values.metricGroupId)
      if (goalError) {
        errors.metricGroupId = goalError
      }

      return errors
    },

    onSubmit: async (values) => {
      const result = await update({
        variables:
          metric.type === 'qualitative'
            ? values
            : {
                ...values,
                startValue: values.startValue,
                targetValue: values.targetValue,
              },
      })
      const handled = handleResult(result, () => null)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      onClose()
      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('performance_goals.update_key_results_modal.title')}
      onClose={onClose}
      illustration="analytics-graph-lines"
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={() => onClose()} type="secondary" disabled={form.isSubmitting}>
            {i18n.t('performance_goals.update_key_results_modal.actions.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('performance_goals.update_key_results_modal.actions.update')}
          </Button>
        </>
      }
    >
      <Box gap="s12">
        <TextField
          error={form.errors.title?.toString()}
          label={i18n.t('performance_goals.update_key_results_modal.fields.title')}
          value={form.values.title}
          onChange={(value) => form.setFieldValue('title', value)}
        />
        <Box flexDirection="row" gap="s12">
          <DayPicker
            value={form.values.startOn}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.update_key_results_modal.fields.start_on')}
            mode="single"
            onChange={(date) => {
              form.setValues({ ...form.values, startOn: date })
            }}
          />
          <DayPicker
            value={form.values.endOn}
            error={form.errors.endOn?.toString()}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.update_key_results_modal.fields.end_on')}
            mode="single"
            onChange={(date) => {
              form.setValues({ ...form.values, endOn: date })
            }}
          />
        </Box>
        {metric.type === 'quantitative' && (
          <Box gap="s12" flexDirection="row">
            <NumberField
              label={i18n.t('performance_goals.update_key_results_modal.fields.startValue')}
              value={form.values.startValue}
              maxDecimals={2}
              onChange={(value) => {
                form.setValues({
                  ...form.values,
                  startValue: value ?? 0,
                })
              }}
            />
            <NumberField
              label={i18n.t('performance_goals.update_key_results_modal.fields.targetValue')}
              value={form.values.targetValue}
              maxDecimals={2}
              onChange={(value) => {
                form.setValues({
                  ...form.values,
                  targetValue: value ?? 0,
                })
              }}
            />
          </Box>
        )}
        <MetricGroupSelectField
          value={form.values.metricGroupId?.toString()}
          onChange={(value) => form.setFieldValue('metricGroupId', value)}
          error={form.errors.metricGroupId?.toString()}
          touched={form.touched.metricGroupId}
          defaultMetricGroup={metric.metricGroup}
        />
      </Box>
    </Modal>
  )
}
